import { memo, useCallback, useEffect, useState } from 'react';

import { Modal } from '@air/primitive-modal';
import { useToasts } from '@air/provider-toast';

export type DemoSchedulerModalProps = {
  schedulerUrl: string;
};

export const DemoSchedulerModal = memo(({ onClose, schedulerUrl }: AirModalProps<DemoSchedulerModalProps>) => {
  const { showToast } = useToasts();
  const [schedulerTarget, setSchedulerTarget] = useState<HTMLDivElement | null>(null);

  const handleMeetingBooked = useCallback(() => {
    showToast('Your demo is scheduled.');
    onClose();
  }, [onClose, showToast]);

  useEffect(() => {
    const display = window.DefaultPixelSDK?.scheduler?.display;

    if (!schedulerTarget || !display) {
      return;
    }

    return display(schedulerUrl, { target: schedulerTarget, loader: true });
  }, [schedulerTarget, schedulerUrl]);

  useEffect(() => {
    const scheduler = window.DefaultPixelSDK?.scheduler;

    if (!scheduler) {
      return;
    }

    const unsubscribeClosed = scheduler.onClosed(onClose);
    const unsubscribeMeetingBooked = scheduler.onMeetingBooked(handleMeetingBooked);

    return () => {
      unsubscribeClosed();
      unsubscribeMeetingBooked();
    };
  }, [handleMeetingBooked, onClose]);

  return (
    <Modal
      className="mt-[3vh] flex h-[90vh] flex-col overflow-hidden p-0"
      dangerouslyBypassFocusLock
      data-testid="DEMO_SCHEDULER_MODAL"
      isOpen
      onDismiss={onClose}
      size="large"
    >
      {/* The SDK iframe is height 100%, so this box needs a real height or the calendar collapses. */}
      <div className="h-full min-h-0 w-full" ref={setSchedulerTarget} />
    </Modal>
  );
});

DemoSchedulerModal.displayName = 'DemoSchedulerModal';
